import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { 
  FaChevronDown, 
  FaRegClipboard, 
  FaRegClock, 
  FaRegCheckSquare, 
  FaShieldAlt, 
  FaCheck
} from "react-icons/fa";
import "../Styles/VeaPromises.css";
import "../Styles/Animations.css";
import Reveal, { RevealGroup, RevealItem } from "./Reveal";
import MagneticButton from "./MagneticButton";

const trustPillars = [
  { title: "Ethical Advice", schedule: "Always" },
  { title: "Total Clarity", schedule: "Before you sign" },
  { title: "Quality Checklist", schedule: "Every step" },
  { title: "Milestone Updates", schedule: "No follow-ups" },
  { title: "Accountable Care", schedule: "If we fall short" }
];

const promisesList = [
  {
    num: 1,
    title: "Ethical Advice, Always",
    desc: "We only recommend pathways where you stand a realistic chance, pointing out both key benefits and potential hurdles.",
    receive: "Personalized counselling assessment report."
  },
  {
    num: 2,
    title: "Total Clarity Before You Sign",
    desc: "Before any financial transaction, you receive a full breakdown of scopes, schedules, fees, and government charges in writing.",
    receive: "Written service agreement & fee schedules."
  },
  {
    num: 3,
    title: "Checklist-Driven Quality",
    desc: "We execute your documentation through rigorous validation checklists to prevent errors, delays, or rejections.",
    receive: "Milestone checklist verification updates."
  },
  {
    num: 4,
    title: "Milestones & Proactive Updates",
    desc: "We keep you informed at every critical juncture of your application, meaning you never have to ask us for status reports.",
    receive: "System-triggered status updates."
  },
  {
    num: 5,
    title: "Accountable & Corrective Support",
    desc: "If any of our processes fall below our committed service standards, we prioritize rectifying it with corrective actions, including rework.",
    receive: "Escalation review and corrective plan."
  }
];

const warningLimits = [
  "Guarantees on visa outcomes, admission offers, or foreign jobs.",
  "Unrealistic or fast-tracked processing claims.",
  "Hidden costs or undisclosed vendor fees."
];

const serviceStandards = [
  {
    icon: <FaRegClipboard />,
    title: "Scope & Agreements",
    desc: "Before start"
  },
  {
    icon: <FaRegClock />,
    title: "Responsive Care",
    desc: "Proactive updates"
  },
  {
    icon: <FaRegCheckSquare />,
    title: "Multi-Level Verification",
    desc: "Before submissions"
  },
  {
    icon: <FaShieldAlt />,
    title: "Quick Resolutions",
    desc: "If a concern arises"
  }
];

const faqList = [
  {
    q: "Can you guarantee a visa?",
    a: "No, visa approvals are decided strictly by high commissions and embassies. We guarantee expert preparation to maximize your success probability."
  },
  {
    q: "What will I receive before making a payment?",
    a: "A written quote and scope sheet specifying the service scope, fee components, exclusions, and subsequent milestones."
  },
  {
    q: "Do I have to follow up for updates?",
    a: "No, our consultants communicate proactively at every milestone. You can follow your application step-by-step."
  },
  {
    q: "What happens if there's a process delay?",
    a: "If a delay is caused on our side, we escalate the case immediately, keep you informed, and implement a corrective resolution plan."
  }
];

export default function VeaPromises() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className="vea-promises-body">
      <Helmet>
        <title>Our Promises & Commitments | Vidya Education Abroad (VEA)</title>
        <meta name="description" content="Read the VEA Promises. We stand for ethical advice, total clarity before you sign, checklist-driven quality, proactive milestones, and accountable support." />
        <link rel="canonical" href="https://veabroad.com/vea-promises" />
        <meta property="og:title" content="Our Promises & Commitments | Vidya Education Abroad (VEA)" />
        <meta property="og:description" content="Read the VEA Promises. We stand for ethical advice, total clarity before you sign, checklist-driven quality, proactive milestones, and accountable support." />
        <meta property="og:url" content="https://veabroad.com/vea-promises" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Banner Section */}
      <section className="promises-hero">
        <div className="promises-hero-grid" />
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ width: "400px", height: "400px", top: "-150px", right: "-100px" }} />
          <div className="va-blob va-blob-teal" style={{ width: "350px", height: "350px", bottom: "-100px", left: "-80px" }} />
        </div>

        <div className="container position-relative z-3">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Our Promises <span className="highlight va-shine-text va-shine-text-on-dark">to You</span></h1>
            <div className="tagline">Honest advice | Transparent process | Diligent execution</div>
            <p className="subtitle">
              At Vidya Education Abroad (VEA), our commitments are built on your peace of mind. We stand for integrity, process transparency, and dedicated support for your global journey. Our VEA Promises ensure a clear scope, milestone tracking, and high-quality validation procedures that keep your application on path. We do not promise guarantees out of our hands, but we do promise a reliable, checklist-driven process so you can progress with absolute clarity.
            </p>
          </motion.div>

          {/* Pillars List */}
          <motion.div 
            className="pillars-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {trustPillars.map((pillar, i) => (
              <motion.div 
                key={i} 
                className="pillar-item"
                variants={itemVariants}
              >
                <strong>{pillar.title}</strong>
                <span>{pillar.schedule}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5 Promises Section */}
      <section className="promises-section">
        <div className="container">
          <div className="section-title-wrapper">
            <h2 className="section-title">Our 5 Core Commitments</h2>
            <p className="section-subtitle">Dedicated principles designed to support your global dreams</p>
          </div>

          <motion.div 
            className="promises-list"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            {promisesList.map((promise) => (
              <motion.div 
                key={promise.num} 
                className="promise-card"
                variants={itemVariants}
              >
                <div className="promise-badge">{promise.num}</div>
                <div className="promise-content">
                  <h3>{promise.title}</h3>
                  <p>{promise.desc}</p>
                  <div className="promise-receipt">
                    <span className="label">What you will receive:</span>
                    <p>{promise.receive}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Warnings / Service Limits Section */}
      <section className="warning-limit-section">
        <div className="container">
          <motion.div 
            className="warning-limit-card"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Clear Service Limits</h2>
            <p className="warning-limit-tagline">We believe in complete transparency and do not promise what is beyond our power.</p>

            <ul className="warning-limit-list">
              {warningLimits.map((item, i) => (
                <li key={i}>
                  <strong>{item}</strong>
                </li>
              ))}
            </ul>

            <div className="truth-box">
              <div className="truth-icon">
                <FaCheck style={{ color: "var(--vea-success-border)" }} />
              </div>
              <p className="truth-text">
                <strong>The Truth:</strong> Final visa decisions belong to government immigration authorities. We promise expert guidance, error-free applications, and absolute integrity.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service Standards Section */}
      <section className="standards-section">
        <div className="container">
          <div className="section-title-wrapper">
            <h2 className="section-title">Our Quality Standards</h2>
            <p className="section-subtitle">What you can always expect from the VEA team</p>
          </div>

          <motion.div 
            className="standards-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            {serviceStandards.map((std, i) => (
              <motion.div 
                key={i} 
                className="standard-card hover-lift"
                variants={itemVariants}
              >
                <div className="standard-icon-wrapper va-float">
                  {std.icon}
                </div>
                <h4>{std.title}</h4>
                <p>{std.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Concern Section */}
      <section className="concern-banner">
        <Reveal as="div" direction="zoom" className="container concern-container">
          <h2>Need to Escalate a Concern?</h2>
          <p>Your satisfaction is our focus. If something is not on track, let us know and we will resolve it with top priority.</p>
          <Link to="/faq" className="btn-concern">
            Contact Support & Resolve Issue
          </Link>
        </Reveal>
      </section>

      {/* Trust FAQs Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-title-wrapper">
            <h2 className="section-title">Commitment FAQs</h2>
            <p className="section-subtitle">Common questions regarding our client promises and guidelines</p>
          </div>

          <RevealGroup as="div" className="faq-list" stagger={0.1} amount={0.15}>
            {faqList.map((faq, i) => (
              <RevealItem
                as="div"
                key={i}
                direction="up"
                className={`faq-accordion-item va-hover-lift ${openFaq === i ? 'open' : ''}`}
                onClick={() => toggleFaq(i)}
              >
                <div className="faq-accordion-header">
                  <h4>{faq.q}</h4>
                  <div className="faq-accordion-icon">
                    <FaChevronDown />
                  </div>
                </div>
                
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="faq-accordion-content"
                    >
                      <div className="faq-accordion-body">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* CTA Booking Section */}
      <section className="booking-cta-section">
        <div className="container">
          <motion.div 
            className="booking-cta-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Start With an Honest Assessment</h2>
            <p>The best way to begin is a profiling session built to help you decide—without pressure.</p>
            <MagneticButton as="span" className="d-inline-block">
              <Link to="/consultationForm" className="btn-booking va-shimmer-btn">
                Book a Counselling Assessment
              </Link>
            </MagneticButton>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
